import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { fr } from 'date-fns/locale';

import Layout from './components/Layout';
import LoginForm from './components/LoginForm';
import DashboardPage from './pages/DashboardPage';
import FoundObjectsPage from './pages/FoundObjectsPage';
import ProviderEventsPage from './pages/ProviderEventsPage';
import SettingsPage from './pages/SettingsPage';
import MatchesPage from './pages/MatchesPage';
import MessagesPage from './pages/MessagesPage';
import LinkedAnnouncementsPage from './pages/LinkedAnnouncementsPage';
import ProviderPlacesPage from './pages/ProviderPlacesPage';
import ProviderMenuFeatureRoute from './components/ProviderMenuFeatureRoute';
import ResetPasswordPage from './pages/ResetPasswordPage';
import SelectProviderPage from './pages/SelectProviderPage';
import apiService from './services/api';
import { ProviderInfo } from './types/foundObject';

import { createSherlookTheme } from './theme/createSherlookTheme';
import { DASHBOARD_BRAND_PRIMARY } from './theme/dashboardBrand';
import {
  extractHttpStatus,
  recordPollingFailure,
  recordPollingSuccess,
  type PollingEndpointKey,
} from './lib/pollingManager';

const DEFAULT_THEME_COLORS = {
  primary: DASHBOARD_BRAND_PRIMARY,
  secondary: '#3366FF',
  background: '#F9FAFB',
};

const HEX_COLOR_RE = /^#[0-9A-Fa-f]{6}$/;

function safeHexColor(value: unknown, fallback: string): string {
  return typeof value === 'string' && HEX_COLOR_RE.test(value) ? value : fallback;
}

// Configuration de React Query
const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onSuccess: (_data, query) => {
      const endpoint = query.meta?.pollingEndpoint as PollingEndpointKey | undefined;
      if (endpoint) {
        recordPollingSuccess(endpoint);
      }
    },
    onError: (error, query) => {
      const endpoint = query.meta?.pollingEndpoint as PollingEndpointKey | undefined;
      if (endpoint) {
        recordPollingFailure(endpoint, error);
      }
    },
  }),
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        const status = extractHttpStatus(error);
        if (status !== undefined && status >= 500) {
          return false;
        }
        return failureCount < 1;
      },
      retryDelay: (attemptIndex) => Math.min(30_000, 2000 * 2 ** attemptIndex),
      refetchOnWindowFocus: false,
      refetchIntervalInBackground: false,
    },
  },
});

// Composant pour protéger les routes (aligné sur l’API : partenaire / admin lieu / super admin)
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  if (!apiService.canAccessProviderDashboard()) {
    return <Navigate to="/login" replace />;
  }
  if (
    apiService.isCurrentUserSuperAdmin() &&
    apiService.getProviderDashboardContextId() === null
  ) {
    return <Navigate to="/select-provider" replace />;
  }
  return <>{children}</>;
};

const SelectProviderGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  if (!apiService.canAccessProviderDashboard()) {
    return <Navigate to="/login" replace />;
  }
  if (!apiService.isCurrentUserSuperAdmin()) {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
};

// Ne pas rediriger vers /dashboard si le token est valide mais le rôle ne donne pas accès au dashboard
// (sinon boucle infinie avec ProtectedRoute → /login → PublicRoute → /dashboard).
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  if (apiService.canAccessProviderDashboard()) {
    if (
      apiService.isCurrentUserSuperAdmin() &&
      apiService.getProviderDashboardContextId() === null
    ) {
      return <Navigate to="/select-provider" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
};

const AppContent: React.FC = () => {
  const canLoadProviderTheme =
    apiService.canAccessProviderDashboard() &&
    (!apiService.isCurrentUserSuperAdmin() ||
      apiService.getProviderDashboardContextId() !== null);

  const { data: providerInfo } = useQuery({
    queryKey: ['provider-theme-settings'],
    queryFn: () => apiService.getProviderInfo(),
    enabled: canLoadProviderTheme,
    staleTime: 60_000,
    select: (response) => response.data as ProviderInfo,
  });

  const interfaceTheme = (providerInfo?.settings?.interface_theme || {}) as Record<string, unknown>;

  const theme = React.useMemo(
    () =>
      createSherlookTheme({
        primary: safeHexColor(interfaceTheme.primary, DEFAULT_THEME_COLORS.primary),
        secondary: safeHexColor(interfaceTheme.secondary, DEFAULT_THEME_COLORS.secondary),
        background: safeHexColor(interfaceTheme.background, DEFAULT_THEME_COLORS.background),
      }),
    [interfaceTheme.background, interfaceTheme.primary, interfaceTheme.secondary]
  );

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <Routes>
            {/* Route publique - Connexion */}
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <LoginForm />
                </PublicRoute>
              }
            />
            <Route path="/reset-password" element={<ResetPasswordPage />} />

            <Route
              path="/select-provider"
              element={
                <SelectProviderGate>
                  <SelectProviderPage />
                </SelectProviderGate>
              }
            />

            {/* Routes protégées */}
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Routes>
                      <Route index element={<Navigate to="/dashboard" replace />} />
                      <Route path="dashboard" element={<DashboardPage />} />
                      <Route
                        path="found-objects"
                        element={
                          <FoundObjectsPage
                            key="found-objects"
                            title="Objets trouvés"
                            lockedStatus="found"
                          />
                        }
                      />
                      <Route path="storage" element={<ProviderPlacesPage />} />
                      <Route path="places" element={<Navigate to="/storage" replace />} />
                      <Route
                        path="returned-objects"
                        element={
                          <FoundObjectsPage
                            key="returned-objects"
                            title="Objets restitués"
                            lockedStatus="claimed"
                          />
                        }
                      />
                      <Route
                        path="objects-to-send"
                        element={
                          <FoundObjectsPage
                            key="objects-to-send"
                            title="Objets à expédier"
                            lockedStatus="to_ship"
                          />
                        }
                      />
                      <Route
                        path="objects-in-transit"
                        element={
                          <FoundObjectsPage
                            key="objects-in-transit"
                            title="Parti en livraison"
                            lockedStatus="in_transit"
                          />
                        }
                      />
                      <Route path="provider-events" element={<ProviderEventsPage />} />
                      <Route
                        path="matches"
                        element={
                          <ProviderMenuFeatureRoute feature="matchings_enabled">
                            <MatchesPage />
                          </ProviderMenuFeatureRoute>
                        }
                      />
                      <Route path="messages" element={<MessagesPage />} />
                      <Route
                        path="linked-announcements"
                        element={
                          <ProviderMenuFeatureRoute feature="linked_announcements_enabled">
                            <LinkedAnnouncementsPage />
                          </ProviderMenuFeatureRoute>
                        }
                      />
                      <Route path="settings" element={<SettingsPage />} />
                    </Routes>
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Redirection par défaut */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Router>
      </LocalizationProvider>
    </ThemeProvider>
    </StyledEngineProvider>
  );
};

const App: React.FC = () => (
  <QueryClientProvider client={queryClient}>
    <AppContent />
  </QueryClientProvider>
);

export default App;